import { LIMONADE_EYE_REPORT_SCHEMA, } from './types.js';
export function createSessionReport(frames, options = {}) {
    const analyzerModules = [...new Set(frames.flatMap((frame) => frame.analyzers.map((result) => result.module_id)))];
    const warnings = frames.flatMap((frame) => frame.warnings);
    return {
        schema: LIMONADE_EYE_REPORT_SCHEMA,
        session_id: options.sessionId ?? globalThis.crypto?.randomUUID?.() ?? `eye-${Date.now()}`,
        created_at: options.createdAt ?? new Date().toISOString(),
        source: {
            type: options.source?.type ?? 'unknown',
            name: options.source?.name ?? null,
            width: options.source?.width ?? frames[0]?.width ?? null,
            height: options.source?.height ?? frames[0]?.height ?? null,
            fps: options.source?.fps ?? null,
            duration_ms: options.source?.duration_ms ?? null,
        },
        summary: {
            frames_analyzed: frames.length,
            analyzer_modules: analyzerModules,
            average_screen_quality: null,
            highest_spill_risk: 'unknown',
            most_common_lighting_setup: 'unknown',
            average_edge_confidence: null,
            exposure_status_summary: {
                good: 0,
                underexposed: 0,
                overexposed: 0,
                too_contrasty: 0,
                too_flat: 0,
            },
            total_warnings: warnings.length,
            warning_count: warnings.length,
            worst_frames: [],
        },
        warnings,
        frames: [...frames],
    };
}
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const isNumber = (value) => typeof value === 'number' && Number.isFinite(value);
export function isWarningResult(value) {
    if (!isRecord(value))
        return false;
    return (typeof value.code === 'string' &&
        ['info', 'warning', 'error'].includes(String(value.severity)) &&
        typeof value.message === 'string' &&
        (value.module_id === undefined || typeof value.module_id === 'string') &&
        (value.frame_number === undefined || Number.isInteger(value.frame_number)));
}
export function isOverlayPrimitive(value) {
    if (!isRecord(value))
        return false;
    return ([
        'box', 'polygon', 'mask', 'heatmap', 'arrow', 'point', 'line', 'text',
        'vector_field', 'rectangle', 'circle',
    ].includes(String(value.type)) &&
        Array.isArray(value.points) &&
        value.points.every((point) => isRecord(point) && isNumber(point.x) && isNumber(point.y)) &&
        (value.opacity === undefined || (isNumber(value.opacity) && value.opacity >= 0 && value.opacity <= 1)) &&
        (value.radius === undefined || (isNumber(value.radius) && value.radius >= 0)));
}
export function isAnalyzerResult(value) {
    if (!isRecord(value))
        return false;
    return (typeof value.module_id === 'string' &&
        typeof value.module_name === 'string' &&
        isNumber(value.confidence) && value.confidence >= 0 && value.confidence <= 1 &&
        Number.isInteger(value.frame_number) &&
        isNumber(value.timestamp_ms) &&
        isRecord(value.metrics) &&
        Array.isArray(value.warnings) && value.warnings.every(isWarningResult) &&
        Array.isArray(value.overlays) && value.overlays.every(isOverlayPrimitive));
}
export function isLimonadeEyeFrameResult(value) {
    if (!isRecord(value))
        return false;
    return (Number.isInteger(value.frame_number) &&
        isNumber(value.timestamp_ms) &&
        isNumber(value.width) && value.width > 0 &&
        isNumber(value.height) && value.height > 0 &&
        isNumber(value.processing_duration_ms) &&
        isRecord(value.analyzer_timings_ms) && Object.values(value.analyzer_timings_ms).every(isNumber) &&
        Array.isArray(value.analyzers) && value.analyzers.every(isAnalyzerResult) &&
        Array.isArray(value.warnings) && value.warnings.every(isWarningResult) &&
        Array.isArray(value.overlays) && value.overlays.every(isOverlayPrimitive));
}
export function isLimonadeEyeSessionReport(value) {
    if (!isRecord(value) || !isRecord(value.source) || !isRecord(value.summary))
        return false;
    return (value.schema === LIMONADE_EYE_REPORT_SCHEMA &&
        typeof value.session_id === 'string' && value.session_id.length > 0 &&
        typeof value.created_at === 'string' &&
        (value.source.name === null || typeof value.source.name === 'string') &&
        ['video', 'image_sequence', 'unknown'].includes(String(value.source.type)) &&
        (value.source.width === null || (isNumber(value.source.width) && value.source.width > 0)) &&
        (value.source.height === null || (isNumber(value.source.height) && value.source.height > 0)) &&
        (value.source.fps === null || (isNumber(value.source.fps) && value.source.fps > 0)) &&
        (value.source.duration_ms === null || isNumber(value.source.duration_ms)) &&
        Array.isArray(value.warnings) && value.warnings.every(isWarningResult) &&
        Array.isArray(value.frames) && value.frames.every(isLimonadeEyeFrameResult) &&
        value.summary.frames_analyzed === value.frames.length &&
        Array.isArray(value.summary.analyzer_modules) && value.summary.analyzer_modules.every((id) => typeof id === 'string') &&
        isNumber(value.summary.warning_count) &&
        isNumber(value.summary.total_warnings) &&
        Array.isArray(value.summary.worst_frames));
}
//# sourceMappingURL=report.js.map